// 슬라이드 문구 수정 — 표지 훅·시리즈 라인, 매장 설명 2줄.
//
// 폰트·자간·그림자는 서버가 기존 규격 그대로 다시 그린다. 여기서는 글자만 받는다.
import { useEffect, useState } from "react";
import { Check, LoaderCircle, Type } from "lucide-react";
import type { FoodAsset, FoodPost, FoodSlideText } from "../shared/ootd-review";

const sameText = (a: FoodSlideText, b: FoodSlideText) => JSON.stringify(a) === JSON.stringify(b);

export function FoodTextEditor({ post, onChanged, onError }: {
  post: FoodPost;
  onChanged: (post: FoodPost) => void;
  onError: (message: string) => void;
}) {
  const editable = post.assets.filter((a) => a.editable);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FoodSlideText>({});
  const [busy, setBusy] = useState(false);

  const asset = editable.find((a) => a.id === openId) ?? null;
  useEffect(() => { setDraft(asset?.text ? { ...asset.text } : {}); }, [asset?.id]);

  if (editable.length === 0) {
    return (
      <p className="ft-none">
        이 게시물은 예전 방식으로 만들어져 문구를 고칠 수 없어요. 새 렌더로 다시 적재하면 가능해요.
      </p>
    );
  }

  const save = async () => {
    if (!asset) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/food/${encodeURIComponent(post.id)}/slides/${encodeURIComponent(asset.id)}/text`, {
        method: "POST", credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(draft),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) {
        throw new Error(`${body.error ?? "문구를 고치지 못했어요"} (HTTP ${res.status}${body.code ? " · " + body.code : ""})`);
      }
      if (body.post) onChanged(body.post as FoodPost);
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const line = (label: string, value: string, set: (v: string) => void, hint?: string) => (
    <label className="ft-field" key={label}>
      <span>{label}{hint && <em>{hint}</em>}</span>
      <input value={value} onChange={(e) => set(e.target.value)} spellCheck={false} />
    </label>
  );

  const setTitleLine = (i: number, v: string) => setDraft((d) => {
    const lines = [...(d.titleLines ?? ["", "", ""])];
    lines[i] = v;
    return { ...d, titleLines: lines };
  });
  const setDescLine = (i: number, v: string) => setDraft((d) => {
    const lines = [...(d.descriptionLines ?? ["", ""])];
    lines[i] = v;
    return { ...d, descriptionLines: lines };
  });

  const dirty = asset ? !sameText(draft, asset.text ?? {}) : false;

  return (
    <div className="ft">
      <div className="ft-tabs">
        {editable.map((a: FoodAsset, i) => (
          <button
            key={a.id}
            className={`ft-tab ${openId === a.id ? "on" : ""}`}
            onClick={() => setOpenId(openId === a.id ? null : a.id)}
          >
            {i + 1}. {a.role === "cover" ? "표지" : a.label.split(" | ")[0]}
          </button>
        ))}
      </div>

      {asset && (
        <div className="ft-panel">
          {asset.role === "cover" ? (
            <>
              {line("훅 1줄", (draft.titleLines ?? [])[0] ?? "", (v) => setTitleLine(0, v))}
              {line("훅 2줄", (draft.titleLines ?? [])[1] ?? "", (v) => setTitleLine(1, v))}
              {line("시리즈 라인", (draft.titleLines ?? [])[2] ?? "", (v) => setTitleLine(2, v), "가장 크게 들어가요")}
              {line("강조할 단어", draft.highlightText ?? "", (v) => setDraft((d) => ({ ...d, highlightText: v })), "훅에서 색이 바뀌어요")}
              {line("이모지", draft.seriesEmoji ?? "", (v) => setDraft((d) => ({ ...d, seriesEmoji: v })))}
            </>
          ) : (
            <>
              {line("상호", draft.name ?? "", (v) => setDraft((d) => ({ ...d, name: v })))}
              {line("지역", draft.area ?? "", (v) => setDraft((d) => ({ ...d, area: v })))}
              {line("주소", draft.address ?? "", (v) => setDraft((d) => ({ ...d, address: v })))}
              {line("영업시간", draft.hours ?? "", (v) => setDraft((d) => ({ ...d, hours: v })), "확인된 것만. 비우면 안 나와요")}
              {line("설명 1줄", (draft.descriptionLines ?? [])[0] ?? "", (v) => setDescLine(0, v))}
              {line("설명 2줄", (draft.descriptionLines ?? [])[1] ?? "", (v) => setDescLine(1, v))}
            </>
          )}
          <div className="ft-actions">
            <button className="primary" disabled={!dirty || busy} onClick={() => void save()}>
              {busy ? <LoaderCircle className="spin" size={14} /> : <Check size={14} />}
              {busy ? "다시 그리는 중" : "적용"}
            </button>
            <button className="ghost" disabled={busy} onClick={() => setDraft(asset.text ? { ...asset.text } : {})}>
              되돌리기
            </button>
            <span className="ft-note"><Type size={12} /> 폰트·자간·그림자는 그대로 유지돼요</span>
          </div>
        </div>
      )}
    </div>
  );
}
